import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";

function currentMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function PayrollApprovals() {
    const [month, setMonth] = useState(currentMonth());
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(false);
    const [approveTarget, setApproveTarget] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/payroll/approvals", { params: { month } });
            setPending(data);
            setSelected(new Set());
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [month]);
    useEffect(() => { refresh(); }, [refresh]);

    const totals = useMemo(() => ({
        count: pending.length,
        net: pending.reduce((s, r) => s + (r.net_payable || 0), 0),
        base: pending.reduce((s, r) => s + (r.prorated_base || 0), 0),
        ot: pending.reduce((s, r) => s + (r.ot_amount || 0), 0),
    }), [pending]);

    const selectedNet = useMemo(
        () => pending.filter((r) => selected.has(r.id)).reduce((s, r) => s + (r.net_payable || 0), 0),
        [pending, selected]
    );
    const allChecked = pending.length > 0 && selected.size === pending.length;

    const toggleOne = (id) => {
        setSelected((prev) => {
            const nx = new Set(prev);
            if (nx.has(id)) nx.delete(id); else nx.add(id);
            return nx;
        });
    };
    const toggleAll = () => {
        setSelected((prev) => (prev.size === pending.length ? new Set() : new Set(pending.map((r) => r.id))));
    };

    const bulkApprove = async (ids, label) => {
        if (!ids.length) return;
        const ok = window.confirm(
            `Approve ${ids.length} draft${ids.length === 1 ? "" : "s"} ${label} as drafted?\n` +
            `Total net payable will be moved to the ledger:\n₹ ${inr(
                pending.filter((r) => ids.includes(r.id)).reduce((s, r) => s + (r.net_payable || 0), 0)
            )}`
        );
        if (!ok) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/payroll/runs/bulk-approve", { ids });
            const approved = data.approved?.length || 0;
            const failed = data.failed?.length || 0;
            if (approved) toast.success(`Approved ${approved} draft${approved === 1 ? "" : "s"}`);
            if (failed) {
                const names = data.failed.map((f) => `${f.employee_name || f.id} — ${f.reason}`).join("\n");
                toast.warning(`${failed} failed:\n${names}`);
            }
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBulkBusy(false); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Payroll approvals"
                sub="Drafts drafted by managers, waiting for HR/Founder sign-off. Approve here to move them to the ledger."
                right={
                    <div className="flex items-center gap-2">
                        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="pra-month" />
                        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="pra-refresh">
                            <ArrowsClockwise size={14} className="mr-2" /> Refresh
                        </Button>
                    </div>
                } />

            <div className="p-8 space-y-6">
                <div className="grid grid-cols-4 gap-4">
                    <Stat label="Pending drafts" value={totals.count} raw />
                    <Stat label="Total base" value={totals.base} />
                    <Stat label="Total OT" value={totals.ot} />
                    <Stat label="Total net payable" value={totals.net} tone="teal" bold />
                </div>

                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b flex items-center gap-4 flex-wrap" style={{ borderColor: "var(--se-border)" }}>
                        <div className="font-serif text-lg mr-auto">
                            {month} — {pending.length} pending
                        </div>
                        {selected.size > 0 ? (
                            <div className="text-xs text-stone-600" data-testid="pra-selection-summary">
                                {selected.size} selected · ₹ {inr(selectedNet)} net
                            </div>
                        ) : null}
                        <Button size="sm" variant="outline"
                                onClick={() => bulkApprove(Array.from(selected), "selected")}
                                disabled={selected.size === 0 || bulkBusy}
                                data-testid="pra-bulk-approve-selected">
                            <CheckCircle size={13} className="mr-1" /> Approve selected ({selected.size})
                        </Button>
                        <Button size="sm"
                                onClick={() => bulkApprove(pending.map((r) => r.id), "on this page")}
                                disabled={pending.length === 0 || bulkBusy}
                                style={{ background: "#16a34a", color: "#fff" }}
                                data-testid="pra-bulk-approve-all">
                            <CheckCircle size={13} className="mr-1" /> Approve all ({pending.length})
                        </Button>
                    </div>
                    <table className="se-table" data-testid="pra-table">
                        <thead>
                            <tr>
                                <th className="w-8">
                                    <input type="checkbox" checked={allChecked}
                                           onChange={toggleAll}
                                           disabled={pending.length === 0}
                                           data-testid="pra-select-all"
                                           aria-label="Select all pending" />
                                </th>
                                <th>Employee</th>
                                <th>Drafted by</th>
                                <th className="text-right">Base (prorated)</th>
                                <th className="text-right">OT (hrs × rate)</th>
                                <th className="text-right">Incentive</th>
                                <th className="text-right">Penalty</th>
                                <th className="text-right">Advance EMI</th>
                                <th className="text-right">Other deductions</th>
                                <th className="text-right">Net payable</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pending.length === 0 ? (
                                <tr><td colSpan="11" className="py-10 text-center text-stone-500">
                                    No pending drafts for {month}. Managers submit drafts on the Payroll page — they land here.
                                </td></tr>
                            ) : pending.map((r) => (
                                <tr key={r.id} data-testid={`pra-row-${r.id}`}
                                    className={selected.has(r.id) ? "bg-stone-50" : ""}>
                                    <td>
                                        <input type="checkbox"
                                               checked={selected.has(r.id)}
                                               onChange={() => toggleOne(r.id)}
                                               data-testid={`pra-select-${r.id}`}
                                               aria-label={`Select ${r.employee_name}`} />
                                    </td>
                                    <td>
                                        <div>{r.employee_name}</div>
                                        <div className="text-[10px] text-stone-500">
                                            {r.employee_code}{r.department ? ` · ${r.department}` : ""}{r.tier ? ` · ${r.tier}` : ""}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="text-xs">{r.drafted_by_name || "—"}</div>
                                        <div className="text-[10px] text-stone-500 capitalize">
                                            {r.drafted_by_role || ""}{r.manager_name ? ` · mgr ${r.manager_name}` : ""}
                                        </div>
                                    </td>
                                    <td className="se-num text-right">
                                        ₹ {inr(r.prorated_base)}
                                        {r.prorate_ratio < 1 ? (
                                            <div className="text-[10px] text-stone-500">×{r.prorate_ratio} · {r.effective_working_days}/{r.working_days_in_month} d</div>
                                        ) : null}
                                    </td>
                                    <td className="se-num text-right">
                                        {r.ot_hours || 0} × ₹{r.ot_rate || 0}
                                        <div className="text-[10px] text-stone-500">= ₹{inr(r.ot_amount)}</div>
                                    </td>
                                    <td className="se-num text-right">
                                        {(r.incentive || 0) > 0 ? <span className="text-emerald-700">+₹{inr(r.incentive)}</span> : "—"}
                                    </td>
                                    <td className="se-num text-right">
                                        {(r.penalty || 0) > 0 ? <span className="text-red-700">−₹{inr(r.penalty)}</span> : "—"}
                                    </td>
                                    <td className="se-num text-right">
                                        {(r.advance_recovery || 0) > 0 ? <span className="text-amber-700">−₹{inr(r.advance_recovery)}</span> : "—"}
                                    </td>
                                    <td className="se-num text-right text-xs">
                                        {(r.tds_deduction || 0) > 0 ? <div>TDS ₹{inr(r.tds_deduction)}</div> : null}
                                        {(r.pf_employee_deduction || 0) > 0 ? <div>PF ₹{inr(r.pf_employee_deduction)}</div> : null}
                                        {(r.pt_deduction || 0) > 0 ? <div>PT ₹{inr(r.pt_deduction)}</div> : null}
                                        {(r.tds_deduction || 0) === 0 && (r.pf_employee_deduction || 0) === 0 && (r.pt_deduction || 0) === 0 ? "—" : null}
                                    </td>
                                    <td className="se-num text-right font-medium" style={{ color: "#0f766e" }}>
                                        ₹ {inr(r.net_payable)}
                                    </td>
                                    <td className="text-right">
                                        <Button size="sm" onClick={() => setApproveTarget(r)}
                                            style={{ background: "#16a34a", color: "#fff" }}
                                            data-testid={`pra-approve-${r.id}`}>
                                            <CheckCircle size={13} className="mr-1" /> Approve
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <ApproveDialog target={approveTarget} onClose={() => setApproveTarget(null)} onSaved={refresh} />
        </>
    );
}

function ApproveDialog({ target, onClose, onSaved }) {
    const [inc, setInc] = useState("0");
    const [pen, setPen] = useState("0");
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (target) { setInc("0"); setPen("0"); } }, [target]);

    const finalNet = useMemo(() => {
        if (!target) return 0;
        const base = Number(target.net_payable || 0);
        return Math.max(0, base + Number(inc || 0) - Number(pen || 0));
    }, [target, inc, pen]);

    if (!target) return null;

    const submit = async () => {
        setSaving(true);
        try {
            await api.post(`/payroll/runs/${target.id}/approve`, {
                founder_incentive: Number(inc), founder_penalty: Number(pen),
            });
            toast.success(`Approved — ₹${inr(finalNet)} ready to post`);
            onClose(); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="pra-approve-dialog">
                <DialogHeader>
                    <DialogTitle>Approve payroll — {target.employee_name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="border rounded-sm p-3 text-xs space-y-1"
                         style={{ borderColor: "var(--se-border)", background: "#f8fafc" }}>
                        <div>Base (×{target.prorate_ratio}): ₹{inr(target.prorated_base)}</div>
                        <div>OT: ₹{target.ot_rate}/hr × {target.ot_hours} = ₹{inr(target.ot_amount)}</div>
                        <div>Manager incentive: +₹{inr(target.incentive)} · penalty: −₹{inr(target.penalty)}</div>
                        {(target.advance_recovery || 0) > 0 ? <div>Advance EMI: −₹{inr(target.advance_recovery)}</div> : null}
                        <div className="font-medium">Manager&apos;s net: ₹{inr(target.net_payable)}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Founder incentive (₹)</Label>
                            <Input type="number" value={inc} onChange={(e) => setInc(e.target.value)} data-testid="pra-founder-inc" /></div>
                        <div><Label>Founder penalty (₹)</Label>
                            <Input type="number" value={pen} onChange={(e) => setPen(e.target.value)} data-testid="pra-founder-pen" /></div>
                    </div>
                    <div className="text-sm">
                        Final net after your adjustment: <b data-testid="pra-final-net">₹ {inr(finalNet)}</b>
                    </div>
                    <div className="text-[11px] text-stone-500">
                        Both manager&apos;s and founder&apos;s adjustments are stored separately in the audit trail.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "#16a34a", color: "#fff" }} data-testid="pra-approve-submit">
                        {saving ? "…" : "Approve"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Stat({ label, value, tone, bold, raw }) {
    const color = tone === "amber" ? "#b45309" : tone === "teal" ? "#0f766e" : "#0f172a";
    return (
        <div className="border rounded-md px-5 py-4" style={{ borderColor: "var(--se-border)" }}>
            <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className={`se-num text-2xl mt-1 ${bold ? "font-medium" : ""}`} style={{ color }}>
                {raw ? value : `₹ ${inr(value || 0)}`}
            </div>
        </div>
    );
}
